import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { SOCKET_URL } from "../../../config/env";
import eventService from "../services/eventService";

export default function useEventSocket({
  // SCHOOLYEAR
  setSchoolyears,
  setSelectedSchoolyear,
  selectedSchoolyearIdRef,

  // EVENTS
  setEvents,
  setSelectedEvent,
  selectedEventIdRef,

  // SHIFTS / JOBS / ANGEBOTE
  loadSommerData,
  setShifts,
  setAngebote,
}) {
  const socketRef = useRef(null);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      reconnection: true,
      reconnectionAttempts: 5,
      timeout: 2000,
    });

    socketRef.current = socket;

    /**
     * =========================================================
     * ✅ SCHOOLYEAR EVENTS
     * =========================================================
     */

    socket.on("schoolyearCreated", async () => {
      const res = await eventService.getSchoolyears();
      setSchoolyears(res);
    });

    socket.on("schoolyearUpdated", async ({ schoolyearId }) => {
      if (schoolyearId !== selectedSchoolyearIdRef.current) return;

      const res = await eventService.getSchoolyears();
      setSchoolyears(res);

      const updated = res.find((s) => s.id === schoolyearId);
      if (updated) setSelectedSchoolyear(updated);
    });

    /**
     * =========================================================
     * ✅ EVENT EVENTS
     * =========================================================
     */

    socket.on("eventCreated", async ({ schoolyearId }) => {
      if (schoolyearId !== selectedSchoolyearIdRef.current) return;

      const res = await eventService.getEvents(schoolyearId);
      setEvents(res);
    });

    socket.on("eventUpdated", ({ event }) => {
      if (Number(event.id) !== Number(selectedEventIdRef.current)) return;

      setSelectedEvent((prev) => ({
        ...prev,
        ...event,
      }));
    });

    socket.on("eventDeleted", ({ eventId }) => {
      if (!eventId) return;

      if (Number(eventId) === Number(selectedEventIdRef.current)) {
        setSelectedEvent(null); // ✅ remove current event
      }

      // ✅ reload list
      loadSommerData(selectedSchoolyearIdRef.current);
    });

    /**
     * =========================================================
     * ✅ SHIFT EVENTS
     * =========================================================
     */

    socket.on("shiftCreated", async ({ eventId }) => {
      if (eventId !== selectedEventIdRef.current) return;

      // ✅ safest: reload full structure
      await loadSommerData(eventId);
    });

    socket.on("shiftUpdated", async ({ eventId }) => {
      if (eventId !== selectedEventIdRef.current) return;

      await loadSommerData(eventId);
    });

    socket.on("shiftDeleted", async ({ eventId }) => {
      if (eventId !== selectedEventIdRef.current) return;

      await loadSommerData(eventId);
    });

    /**
     * =========================================================
     * ✅ JOB EVENTS
     * =========================================================
     */

    socket.on("jobCreated", async ({ eventId }) => {
      if (Number(eventId) !== Number(selectedEventIdRef.current)) return;
      await loadSommerData(eventId);
    });

    socket.on("jobUpdated", async ({ eventId }) => {
      if (Number(eventId) !== Number(selectedEventIdRef.current)) return;
      await loadSommerData(eventId);
    });

    socket.on("jobDeleted", async ({ eventId }) => {
      if (Number(eventId) !== Number(selectedEventIdRef.current)) return;
      await loadSommerData(eventId);
    });

    /**
     * =========================================================
     * ✅ ANGEBOT EVENTS (fine-grained ✅)
     * =========================================================
     */

    socket.on("angebotUpdated", ({ angebotId, reservedBy }) => {
      setAngebote((prev) =>
        prev.map((a) => (a.id !== angebotId ? a : { ...a, reservedBy })),
      );
    });

    /**
     * =========================================================
     * ✅ CLEANUP
     * =========================================================
     */

    return () => {
      socket.disconnect();
    };
  }, []);
}
